import { useCallback, useEffect, useRef } from 'react';

// Longest the frozen editor frame is held waiting for the saved view image before switching anyway.
// This is a safety valve for loads that never arrive, not pacing: expiring while the render is still
// processing reveals an unloaded view (stale/placeholder content), so it errs generous — slow media
// holds the correct frozen frame instead of flashing the previous version.
const SAVE_VIEW_LOAD_FALLBACK_MS = 8000;

/**
 * Only persisted media URLs qualify as "the saved view source". The view image also fires loads for
 * transient paints — a 1px data: placeholder while a fresh source decodes, or a blob: preview cover —
 * and treating one of those as the saved render completes the hand-off early: the editor unmounts and
 * the view sits on the placeholder (briefly showing stale content) until the real render arrives.
 */
const isPersistedViewSource = (sourceUrl: string): boolean =>
    !sourceUrl.startsWith('data:') && !sourceUrl.startsWith('blob:');

/**
 * Coordinates the post-save hand-off from the (frozen) editor to the view image.
 *
 * After a save persists, `finishEditingAfterSave` holds the current mode until the view image has loaded the
 * newly-saved source (`handleViewImageLoaded`, called from the view's onload) — otherwise switching to view
 * mode immediately reveals the view still showing the previous orientation mid-load. A fallback timer still
 * completes the switch if that load never arrives.
 */
export const useFinishEditingAfterViewLoad = (finishEditing: () => void) => {
    const awaitingRef = useRef(false);
    const saveStartedRef = useRef(false);
    const savePersistedRef = useRef(false);
    const hasLoadedSavedSourceRef = useRef(false);
    const latestLoadedSourceRef = useRef<string | null>(null);
    // Every source the view loads between save start and persistence. None of these can be the saved
    // render (it does not exist yet) — e.g. a different-size render of the pre-save image requested by a
    // layout change right after Save. Treating one as the saved source completes the hand-off the moment
    // persistence lands, unmounting the editor while the real render is still loading.
    const preSaveSourcesRef = useRef<Set<string>>(new Set());
    // Pathname of the persisted image content the current save produced (set by finishEditingAfterSave).
    const expectedSavedSourcePathRef = useRef<string | null>(null);
    const fallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const completeHandoff = useCallback(() => {
        awaitingRef.current = false;
        saveStartedRef.current = false;
        savePersistedRef.current = false;
        hasLoadedSavedSourceRef.current = false;
        if (fallbackRef.current) clearTimeout(fallbackRef.current);
        fallbackRef.current = null;
        finishEditing();
    }, [finishEditing]);

    const matchesExpectedSource = useCallback((sourceUrl: string): boolean => {
        const expectedPath = expectedSavedSourcePathRef.current;
        return !!expectedPath && sourceUrl.includes(expectedPath);
    }, []);

    const handleViewImageLoaded = useCallback(
        (sourceUrl: string) => {
            if (!isPersistedViewSource(sourceUrl)) return;

            latestLoadedSourceRef.current = sourceUrl;
            if (saveStartedRef.current && !savePersistedRef.current) {
                // Between save start and persistence the view can request new sizes of the PRE-save image
                // (a layout change right after Save) — those must never count as the saved render. Only a
                // load matching the persisted source path (known once the save dispatched it) qualifies;
                // it can beat `finishEditingAfterSave` when the save awaits further uploads afterwards.
                if (matchesExpectedSource(sourceUrl)) hasLoadedSavedSourceRef.current = true;
                else preSaveSourcesRef.current.add(sourceUrl);
                return;
            }
            if (!awaitingRef.current || !savePersistedRef.current) return;
            if (preSaveSourcesRef.current.has(sourceUrl) && !matchesExpectedSource(sourceUrl)) return;

            completeHandoff();
        },
        [completeHandoff, matchesExpectedSource],
    );

    /** Snapshot the sources painted behind the editor before persistence replaces them. */
    const beginSaveHandoff = useCallback(() => {
        saveStartedRef.current = true;
        savePersistedRef.current = false;
        hasLoadedSavedSourceRef.current = false;
        expectedSavedSourcePathRef.current = null;
        preSaveSourcesRef.current = new Set(
            latestLoadedSourceRef.current ? [latestLoadedSourceRef.current] : [],
        );
    }, []);

    /** Abandon a started handoff whose save turned out to be a no-op or failed before persisting. */
    const cancelSaveHandoff = useCallback(() => {
        saveStartedRef.current = false;
        savePersistedRef.current = false;
        hasLoadedSavedSourceRef.current = false;
        expectedSavedSourcePathRef.current = null;
        preSaveSourcesRef.current = new Set();
    }, []);

    /**
     * Requires a preceding `beginSaveHandoff` — the captured sources are what the hand-off waits to
     * replace. `savedSourcePath` is the pathname of the image content the save persisted, so loads of
     * the new render can be told apart from other-size loads of the pre-save image.
     */
    const finishEditingAfterSave = useCallback(
        (savedSourcePath?: string | null) => {
            expectedSavedSourcePathRef.current = savedSourcePath ?? null;
            savePersistedRef.current = true;
            const savedSourceAlreadyLoaded =
                hasLoadedSavedSourceRef.current ||
                [...preSaveSourcesRef.current, latestLoadedSourceRef.current ?? ''].some(matchesExpectedSource);
            if (savedSourceAlreadyLoaded) {
                completeHandoff();
                return;
            }

            awaitingRef.current = true;
            fallbackRef.current = setTimeout(completeHandoff, SAVE_VIEW_LOAD_FALLBACK_MS);
        },
        [completeHandoff, matchesExpectedSource],
    );

    useEffect(() => () => clearTimeout(fallbackRef.current ?? undefined), []);

    return { beginSaveHandoff, cancelSaveHandoff, finishEditingAfterSave, handleViewImageLoaded };
};
